import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Space, Button, Typography, Table, message, Modal, Input } from 'antd';
import dayjs from 'dayjs';
import request from '@/utils/request';
import type { BundleDetailDTO } from '@zero/shared';

const BundleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState<BundleDetailDTO | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');

  const load = async () => {
    const res = await request.get<BundleDetailDTO>(`/admin/bundles/${id}`);
    setBundle(res.data as unknown as BundleDetailDTO);
  };

  useEffect(() => {
    load();
  }, [id]);

  const approve = async () => {
    await request.post(`/admin/bundles/${id}/review`, { action: 'approve' });
    message.success('已通过');
    load();
  };

  const reject = async () => {
    if (!reason.trim()) return message.error('请填写驳回理由');
    await request.post(`/admin/bundles/${id}/review`, { action: 'reject', reason });
    message.success('已驳回');
    setRejectOpen(false);
    setReason('');
    load();
  };

  if (!bundle) return null;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {bundle.title} <Tag>{bundle.status}</Tag>
        </Typography.Title>
        <Space>
          <Button onClick={() => navigate('/bundles')}>返回</Button>
          {bundle.status === 'pending_review' && (
            <>
              <Button type="primary" onClick={approve}>
                通过
              </Button>
              <Button danger onClick={() => setRejectOpen(true)}>
                驳回
              </Button>
            </>
          )}
        </Space>
      </Space>

      <Card>
        <Descriptions column={3}>
          <Descriptions.Item label="创作者">
            {bundle.creatorDisplayName || `#${bundle.creatorId}`}
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag>{bundle.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="价格">
            {bundle.priceRmbCents != null ? `¥${(bundle.priceRmbCents / 100).toFixed(2)}` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="币价">
            {bundle.priceCoins ?? '—'} 币
          </Descriptions.Item>
          <Descriptions.Item label="fal 成本">
            {bundle.totalFalCostUsd != null ? `$${bundle.totalFalCostUsd.toFixed(4)}` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="创作者分成">
            {bundle.creatorShareCents != null
              ? `¥${(bundle.creatorShareCents / 100).toFixed(2)}`
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="销量">{bundle.salesCount}</Descriptions.Item>
          <Descriptions.Item label="热度">{bundle.heat}</Descriptions.Item>
          <Descriptions.Item label="发布时间">
            {bundle.publishedAt ? dayjs(bundle.publishedAt).format('YYYY-MM-DD HH:mm') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="描述" span={3}>
            {bundle.description || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="上传要求" span={3}>
            {bundle.uploadInstruction || '—'}（最少 {bundle.requiredUploadCount} 张，最多{' '}
            {bundle.maxUploadCount} 张）
          </Descriptions.Item>
          {bundle.rejectionReason && (
            <Descriptions.Item label="驳回理由" span={3}>
              {bundle.rejectionReason}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card title={`指令列表（${bundle.instructions.length}）`}>
        <Table
          rowKey="id"
          pagination={false}
          dataSource={bundle.instructions}
          columns={[
            { title: '#', dataIndex: 'orderIndex', width: 60 },
            { title: '标题', dataIndex: 'title' },
            { title: '模型', dataIndex: 'modelSlug', render: (v) => <Tag color="purple">{v}</Tag> },
            {
              title: 'Prompt',
              dataIndex: 'promptText',
              render: (v) =>
                v ? (
                  <Typography.Text ellipsis style={{ maxWidth: 400 }}>
                    {v}
                  </Typography.Text>
                ) : (
                  '—'
                ),
            },
            {
              title: '参数',
              dataIndex: 'paramsJson',
              render: (v) => (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12, maxWidth: 300, display: 'block' }}
                  ellipsis
                >
                  {JSON.stringify(v)}
                </Typography.Text>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        open={rejectOpen}
        title="驳回套图"
        onOk={reject}
        onCancel={() => setRejectOpen(false)}
      >
        <Input.TextArea
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="请填写清晰的驳回理由"
        />
      </Modal>
    </Space>
  );
};

export default BundleDetailPage;
